import { Terminal } from 'lucide-react';
import Image from '@/components/app-image';
import { ModelProviderLogo } from '@/components/model-provider-logo';

type AgentBrand = 'anthropic' | 'cursor' | 'gemini' | 'goose' | 'openai' | 'opencode';

function agentBrand(id: string, name?: string): AgentBrand | null {
  const value = `${id} ${name ?? ''}`.toLowerCase();
  if (value.includes('claude')) return 'anthropic';
  if (value.includes('codex')) return 'openai';
  if (value.includes('cursor')) return 'cursor';
  if (value.includes('opencode')) return 'opencode';
  if (value.includes('gemini')) return 'gemini';
  if (value.includes('goose')) return 'goose';
  return null;
}

export function AgentLogo({
  className,
  iconUrl,
  id,
  name,
}: {
  className?: string;
  iconUrl?: string | null;
  id: string;
  name?: string;
}) {
  const brand = agentBrand(id, name);
  if (brand === 'anthropic') {
    return (
      <Image
        alt=""
        aria-hidden="true"
        className={className}
        height={32}
        src="/agent-icons/claude.svg"
        width={32}
      />
    );
  }
  if (brand === 'openai') {
    return <ModelProviderLogo className={className} provider="openai" />;
  }
  if (brand === 'cursor') {
    return (
      <Image
        alt=""
        aria-hidden="true"
        className={className}
        height={32}
        src="/app-icons/cursor.png"
        width={32}
      />
    );
  }
  if (brand === 'opencode') {
    return (
      <svg aria-hidden="true" className={className} fill="none" viewBox="0 0 16 16">
        <path
          clipRule="evenodd"
          d="M13 14H3V2h10v12Zm-2.5-9.6h-5v7.2h5V4.4Z"
          fill="currentColor"
          fillRule="evenodd"
        />
      </svg>
    );
  }
  if (brand === 'gemini') {
    return (
      <svg aria-hidden="true" className={className} fill="currentColor" viewBox="0 0 24 24">
        <path d="M11.04 19.32Q12 21.51 12 24q0-2.49.93-4.68.96-2.19 2.58-3.81t3.81-2.55Q21.51 12 24 12q-2.49 0-4.68-.93a12.3 12.3 0 0 1-3.81-2.58 12.3 12.3 0 0 1-2.58-3.81Q12 2.49 12 0q0 2.49-.96 4.68-.93 2.19-2.55 3.81a12.3 12.3 0 0 1-3.81 2.58Q2.49 12 0 12q2.49 0 4.68.96 2.19.93 3.81 2.55t2.55 3.81" />
      </svg>
    );
  }
  if (brand === 'goose') {
    return (
      <svg aria-hidden="true" className={className} fill="currentColor" viewBox="0 0 24 24">
        <path d="M20.91 19.39 19.52 18.24a9.04 9.04 0 0 1-1.9-2.21 8.38 8.38 0 0 0-2.81-2.86l-.56-.32a.77.77 0 0 1-.23-.98c.29-.41 1.88-2.12 2.15-2.35.36-.3.76-.54 1.12-.82.18-.14.35-.27.48-.43.43-.5.42-.93.42-.93s-.19-.46-.54-.62c.3 0 .63.1.78.25l.49-.78c.04-.08.06-.21-.02-.29-.08-.08-.22-.06-.3-.02-.4.24-.8.5-1.2.67 0 0-.42-.01-.92.42-.16.13-.3.3-.43.48-.28.37-.52.76-.82 1.12-.23.28-1.94 1.87-2.35 2.16a.76.76 0 0 1-.98-.24l-.33-.56a8.38 8.38 0 0 0-2.8-2.8 9.04 9.04 0 0 1-2.21-1.91L5.36 3.83c-.06-.07-.17-.06-.21.03-.13.25-.38.78-.57 1.5.25.31.8.94 1.45 1.5.08.07.02.18-.05.1a8.6 8.6 0 0 1-1.52-.61 7.7 7.7 0 0 0-.08 1.7c.47.25 1.22.55 2.02.73.06.02.05.1 0 .11-.62.12-1.26.14-1.77.13.1.37.24.81.44 1.19.08.17.17.33.25.5.44-.02.96-.07 1.49-.16.09-.02.13.1.06.2-.35.23-.73.43-1.08.6.2.3.43.57.68.82 0 0 1.23 1.27 1.23 1.47.72-.73 1.9-1.55 3.2-2.26a22.7 22.7 0 0 0-3.2 3.06l-.35.48c-.17.25-.33.51-.46.79-.43.92-1.13 2.79-1.13 2.79-.06.15-.01.3.08.4.1.09.24.13.4.08 0 0 1.86-.71 2.79-1.14.27-.13.54-.28.78-.46l.53-.37a.7.7 0 0 1 .9.07l1.21 1.21c.25.25.52.48.8.69.04.03.1.02.13-.03.17-.35.37-.73.6-1.08.06-.08.17-.03.16.06-.1.52-.14 1.04-.16 1.48.17.09.33.18.5.25.38.2.82.34 1.19.44-.02-.51.01-1.15.13-1.77.01-.05.1-.06.11 0 .18.8.48 1.55.73 2.02.57.07 1.14.05 1.7-.08-.21-.41-.46-.96-.61-1.52-.02-.06.05-.1.1-.05.56.66 1.19 1.2 1.5 1.45.72-.19 1.25-.44 1.5-.57.08-.04.1-.15.03-.21Z" />
      </svg>
    );
  }
  if (iconUrl?.startsWith('https://') === true) {
    const maskImage = `url(${JSON.stringify(iconUrl)})`;
    return (
      <span
        aria-hidden="true"
        className={className}
        style={{
          backgroundColor: 'currentColor',
          maskImage,
          maskPosition: 'center',
          maskRepeat: 'no-repeat',
          maskSize: 'contain',
          WebkitMaskImage: maskImage,
          WebkitMaskPosition: 'center',
          WebkitMaskRepeat: 'no-repeat',
          WebkitMaskSize: 'contain',
        }}
      />
    );
  }
  return <Terminal aria-hidden="true" className={className} />;
}
